// Cardápio do BALCÃO (pedido do dono, 29/09/2026): busca e categorias no topo,
// todas as categorias em sequência no meio (clicar na categoria ROLA até ela; é
// a única área da página que rola) e o carrinho fixo na lateral direita. A
// busca fica sempre com o cursor. O Totem continua com a coluna de categorias
// à esquerda (TelaCardapio). Leitor de código de barras: hooks/useLeitorBalcao.
// Celular (< 768 px, 25/10/2026): busca em cima das categorias, que viram uma
// linha só rolando para o lado; o carrinho lateral some e vira a barra "Pedido"
// no rodapé, que abre o carrinho em tela cheia. Do tablet para cima, nada muda.
import { Search, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import AnimacaoAdicionado from "../components/AnimacaoAdicionado";
import CarrinhoLateral, { BarraPedidoCelular } from "../components/CarrinhoLateral";
import DialogCancelarCompra from "../components/DialogCancelarCompra";
import FaixaCliente from "../components/FaixaCliente";
import GradeCategorias, { montarSecoes } from "../components/GradeCategorias";
import { useAtalhosBalcao } from "../hooks/useAtalhosBalcao";
import { useFocoNaBusca } from "../hooks/useFocoNaBusca";
import { useRolagemCategorias } from "../hooks/useRolagemCategorias";
import PopupProduto from "../popup-produto/PopupProduto";

const CAMPOS = ["INPUT", "TEXTAREA", "SELECT"];

function BarraTopo({ ctx, rolagem, buscaRef, secoes }) {
  const categorias = secoes.map((sec) => sec.nome);
  const { buscaAberta, setBuscaAberta, searchTerm, setSearchTerm, lerCodigo } = ctx;
  const buscar = (texto) => { setSearchTerm(texto); setBuscaAberta(!!texto.trim()); };
  // Com a busca preenchida as seções não estão na tela: limpa e rola no próximo quadro.
  const escolher = (cat) => {
    if (buscaAberta) { buscar(""); setTimeout(() => rolagem.rolarPara(cat), 50); }
    else rolagem.rolarPara(cat);
  };
  // Código de barras digitado à mão + Enter lança o produto, igual ao leitor.
  const enter = (e) => {
    const codigo = searchTerm.replace(/\s/g, "");
    if (e.key !== "Enter" || !lerCodigo || !/^\d{4,}$/.test(codigo)) return;
    e.preventDefault();
    lerCodigo(codigo);
    buscar("");
  };
  return (
    <div className="shrink-0 border-b border-zinc-800 bg-zinc-900/60 px-4 py-3 flex max-md:flex-col items-start gap-3" data-e2e="balcao-topo">
      <div className="relative w-64 xl:w-80 max-md:w-full shrink-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
        <input
          ref={buscaRef}
          value={searchTerm}
          onChange={(e) => buscar(e.target.value)}
          onKeyDown={enter}
          placeholder="Buscar produto ou código"
          autoComplete="off"
          data-e2e="busca-input"
          className="w-full h-10 pl-9 pr-9 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-100 placeholder:text-zinc-500 focus:border-primary focus:outline-none"
        />
        {searchTerm && (
          <button type="button" onClick={() => buscar("")} aria-label="Limpar busca" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-zinc-200">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {/* Quebra em linhas em vez de rolar para o lado: nenhuma rolagem extra.
          No celular é o contrário: uma linha só, rolando para o lado. */}
      <div className="flex-1 flex flex-wrap gap-2 max-md:flex-none max-md:w-full max-md:flex-nowrap max-md:overflow-x-auto scrollbar-hide" data-e2e="balcao-categorias">
        {categorias.map((cat) => {
          // Antes de rolar, a primeira categoria é a que está à vista.
          const ativa = !buscaAberta && (rolagem.ativa ?? categorias[0]) === cat;
          return (
            <button key={cat} type="button" onClick={() => escolher(cat)} data-e2e="cat-item" data-ativa={ativa ? "1" : "0"}
              className={`h-10 px-4 rounded-lg text-sm font-semibold transition-colors max-md:shrink-0 max-md:whitespace-nowrap ${ativa ? "bg-primary text-primary-foreground shadow" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-zinc-100"}`}>
              {cat}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function TelaCardapioBalcao({ ctx }) {
  const {
    telaCls, popupBoasVindas, selectedProduct, popupOpen, setPopupOpen, addToCart, allProductsForPhotos, products, promotions, pizza, addedAnim,
  } = ctx;
  const secoes = useMemo(() => montarSecoes(products, ctx.categoryList), [products, ctx.categoryList]);
  const rolagem = useRolagemCategorias();
  const buscaRef = useRef(null);
  useFocoNaBusca(buscaRef);
  // Enter = Finalizar (com a busca vazia: com texto, o Enter é da busca). Desde
  // 29/09/2026 o Balcão vai direto para "Como deseja consumir?" — sem a
  // conferência, porque o carrinho já fica à vista na lateral.
  // Esc = "Cancelar esta compra?" (pedido do dono, 29/09/2026). Com texto na
  // busca, o primeiro Esc só limpa a busca; carrinho vazio, não faz nada. Esc
  // em outro campo (desconto do carrinho, por exemplo) é do campo e não abre o
  // "Cancelar esta compra?" (30/09/2026).
  const [cancelarAberto, setCancelarAberto] = useState(false);
  const [carrinhoCelular, setCarrinhoCelular] = useState(false);
  useAtalhosBalcao({
    Enter: () => {
      if (ctx.searchTerm.trim() || ctx.cart.length === 0) return false;
      ctx.setScreen("delivery_type");
      return true;
    },
    Escape: (e) => {
      const alvo = e.target;
      if (alvo !== buscaRef.current && CAMPOS.includes(alvo?.tagName)) return false;
      if (ctx.searchTerm.trim()) { ctx.setSearchTerm(""); ctx.setBuscaAberta(false); return true; }
      if (ctx.cart.length === 0) return false;
      setCancelarAberto(true);
      return true;
    },
  });
  // `limpar` do ctx é o do checkout (cliente, nome, pagamento); o carrinho vai à parte.
  const cancelarCompra = () => {
    ctx.setCart([]);
    ctx.limpar();
    setCancelarAberto(false);
    toast.success("Compra cancelada");
  };

  return (
    <div className={`${telaCls} bg-zinc-950 flex flex-col select-none`} data-e2e="balcao-cardapio">
      {popupBoasVindas}
      <BarraTopo ctx={ctx} rolagem={rolagem} buscaRef={buscaRef} secoes={secoes} />
      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 flex flex-col">
          <FaixaCliente ctx={ctx} />
          <GradeCategorias ctx={ctx} rolagem={rolagem} secoes={secoes} />
          <BarraPedidoCelular ctx={ctx} onAbrir={() => setCarrinhoCelular(true)} />
        </div>
        <CarrinhoLateral ctx={ctx} />
      </div>
      {carrinhoCelular && <CarrinhoLateral ctx={ctx} celular onFechar={() => setCarrinhoCelular(false)} />}

      <PopupProduto modulo="Balcao" product={selectedProduct} open={popupOpen} onClose={() => setPopupOpen(false)} onAddToCart={addToCart} allProducts={allProductsForPhotos.length > 0 ? allProductsForPhotos : products} activePromotions={promotions} />
      {pizza.popup}
      <AnimacaoAdicionado addedAnim={addedAnim} />
      <DialogCancelarCompra aberto={cancelarAberto} onFechar={() => setCancelarAberto(false)} onConfirmar={cancelarCompra}
        itens={ctx.cart.length} total={ctx.cartTotal} />
    </div>
  );
}
